// Retailer → Suppliers.
//
// Top tabs = each trade supplier (the supplier "profile" selector). Within a
// supplier, sub-tabs: Discount sheet | Product classes | Contact.
//
// Discount sheet (2026-10-01, migration 0094) is the retailer's discount
// sheet from the supplier, row for row, plus their drilling price per hole —
// see discount-sheet.ts. It is the first tab because it is what pre-fills
// Disc % in the Quote Builder; Product classes is the older per-category
// matrix below, kept as the fallback for anything the sheet does not cover.
// The tabs are the four seeded suppliers plus any catalogue supplier the
// retailer has been granted (/catalogue/suppliers), so a newly onboarded
// supplier appears here without a code change.
//
// The Discounts sub-tab is the Retailer & Supplier Discount Matrix, driven by
// the PG-backed endpoints (replaces the deprecated seed/TKC file-store grid):
//   GET /api/portal/discounts?supplier=<uuid>  → per-class matrix
//        { classes:[{categoryId, className, retailerPct, supplierPct,
//                    hasSupplier, useSupplier, effectivePct}] }
//   PUT /api/portal/discounts  { supplier, categoryId, discountPercent? , useSupplier? }
//        → upserts the retailer's OWN row only (never the supplier's schedule)
//
// effective = use_supplier && supplier set → supplier% ; else retailer% ; else 0.
// The toggle is offered only when the supplier has set a discount (hasSupplier).

import { iconMarkup } from "@tkos/shared-ui";
import { ApiError } from "@tkos/api-client";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import { mountDiscountSheet } from "./discount-sheet";

import "./styles.css";

interface SupplierContact { email: string; phone: string; website: string }
interface Supplier {
  key: string;            // short tab key — "TKC"
  id: string;             // real PG supplier UUID
  name: string;
  tabLabel: string;
  desc: string;
  colour: string;
  logo: string;
  profileUrl: string;
  contact: SupplierContact;
}

interface ClassRow {
  categoryId: string;
  className: string;
  retailerPct: number | null;
  supplierPct: number | null;
  hasSupplier: boolean;
  useSupplier: boolean;
  effectivePct: number;
}
interface MatrixResp { ok: boolean; supplierId: string | null; classes: ClassRow[] }
interface PutResp { ok: boolean; class: Omit<ClassRow, "className"> }

const SEED_SUPPLIERS: readonly Supplier[] = [
  { key: "TKC", id: "00000000-0000-0000-0000-000000000001", name: "TKC Components",
    tabLabel: "TKC", desc: "Trade kitchen components & accessories", colour: "#1E40AF", logo: "TKC",
    profileUrl: "https://tkcomponents.co.uk",
    contact: { email: "trade@tkcomponents.co.uk", phone: "01642 626 999", website: "tkcomponents.co.uk" } },
  { key: "PWS", id: "00000000-0000-0000-0000-000000000002", name: "PWS",
    tabLabel: "PWS", desc: "Doors, handles, sinks & taps", colour: "#DC2626", logo: "PWS",
    profileUrl: "https://pws.co.uk",
    contact: { email: "trade@pws.co.uk", phone: "01325 505 555", website: "pws.co.uk" } },
  { key: "Omega", id: "00000000-0000-0000-0000-000000000003", name: "Omega Kitchens",
    tabLabel: "Omega", desc: "Exceptional British-made kitchens", colour: "#1E3A8A", logo: "Ω",
    profileUrl: "https://omegakitchens.co.uk",
    contact: { email: "trade@omega-plc.co.uk", phone: "01302 765 600", website: "omegakitchens.co.uk" } },
  { key: "LDL", id: "00000000-0000-0000-0000-000000000004", name: "LDL Online",
    tabLabel: "LDL", desc: "Doors, drawers & made-to-measure", colour: "#F59E0B", logo: "LDL",
    profileUrl: "https://ldlonline.co.uk",
    contact: { email: "sales@ldlonline.co.uk", phone: "0151 548 9888", website: "ldlonline.co.uk" } },
];

function escapeHtml(s: string): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}
function fmtPct(v: number): string { return `${Number.isInteger(v) ? v : v.toFixed(1)}%`; }
function isAbortError(err: unknown): boolean { return (err as { name?: string })?.name === "AbortError"; }
function cssEscape(s: string): string {
  const ce = (window as unknown as { CSS?: { escape?: (s: string) => string } }).CSS?.escape;
  return ce ? ce(s) : s.replace(/[^a-zA-Z0-9_-]/g, "_");
}

const suppliersPage: Page = {
  name: "suppliers",
  label: "Suppliers",
  icon: "🏭",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let disposed = false;
    const suppliers = SEED_SUPPLIERS.map((s) => ({ ...s }));
    let activeKey = suppliers[0]!.key;

    // Per-supplier matrix cache + load state.
    const matrix = new Map<string, ClassRow[]>();
    const loadState = new Map<string, "idle" | "loading" | "ready" | "error">();
    const saveTimers = new Map<string, number>();

    const root = document.createElement("section");
    root.className = "sup-root";
    root.innerHTML = `
      <header class="sup-header">
        <div class="sup-header__titles">
          <div class="sup-header__icon" aria-hidden="true">${iconMarkup("package", { size: "md" })}</div>
          <div>
            <h1 class="sup-title">Suppliers</h1>
            <p class="sup-subtitle">Your trade suppliers — your discount sheets, drilling prices and contacts.</p>
          </div>
        </div>
      </header>
      <div class="sup-tabs" role="tablist" aria-label="Suppliers" data-role="tabs"></div>
      <div class="sup-panels" data-role="panels"></div>`;
    container.appendChild(root);

    const tabsEl = root.querySelector<HTMLDivElement>("[data-role='tabs']")!;
    const panelsEl = root.querySelector<HTMLDivElement>("[data-role='panels']")!;

    // ── tabs ──────────────────────────────────────────────────────────────────
    function renderTabs(): void {
      tabsEl.innerHTML = suppliers.map((s) => {
        const active = s.key === activeKey;
        return `<button type="button" role="tab" class="sup-tab${active ? " is-active" : ""}"
          data-key="${escapeHtml(s.key)}" aria-selected="${active}" tabindex="${active ? "0" : "-1"}"
          style="--sup-brand:${s.colour}">
          <span class="sup-tab__chip" aria-hidden="true">${escapeHtml(s.logo)}</span>
          <span class="sup-tab__labels"><span class="sup-tab__name">${escapeHtml(s.tabLabel)}</span>
          <span class="sup-tab__desc">${escapeHtml(s.desc)}</span></span></button>`;
      }).join("");
    }

    // ── panels (scaffold; discounts lazy-loaded) ────────────────────────────────
    function renderPanels(): void {
      panelsEl.innerHTML = suppliers.map(panelHtml).join("");
      for (const s of suppliers) {
        const panel = panelsEl.querySelector<HTMLElement>(`#sup-panel-${cssEscape(s.key)}`);
        if (panel) panel.hidden = s.key !== activeKey;
      }
    }

    function panelHtml(s: Supplier): string {
      return `
        <section id="sup-panel-${escapeHtml(s.key)}" class="sup-panel" role="tabpanel" style="--sup-brand:${s.colour}">
          <header class="sup-panel__head">
            <div class="sup-panel__title">
              <span class="sup-panel__chip" aria-hidden="true">${escapeHtml(s.logo)}</span>
              <div><h2>${escapeHtml(s.name)}</h2><p>${escapeHtml(s.desc)}</p></div>
            </div>
            ${s.profileUrl !== "#" ? `<a class="sup-profile-btn" href="${escapeHtml(s.profileUrl)}" target="_blank" rel="noopener noreferrer">Profile &rarr;</a>` : ""}
          </header>
          <div class="sup-subtabs" role="tablist" aria-label="${escapeHtml(s.name)} sections">
            <button type="button" class="sup-subtab is-active" data-sub="sheet" data-key="${escapeHtml(s.key)}">Discount sheet</button>
            <button type="button" class="sup-subtab" data-sub="discounts" data-key="${escapeHtml(s.key)}">Product classes</button>
            <button type="button" class="sup-subtab" data-sub="contact" data-key="${escapeHtml(s.key)}">Contact</button>
          </div>
          <div class="sup-sub" data-sub="sheet" data-key="${escapeHtml(s.key)}" data-role="sheet"></div>
          <div class="sup-sub" data-sub="discounts" data-key="${escapeHtml(s.key)}" hidden>
            <div class="sup-matrix-tools">
              <input type="search" class="sup-matrix-search" placeholder="Filter classes…" data-key="${escapeHtml(s.key)}" />
              <span class="sup-matrix-hint">Effective discount auto-applies in the Quote Builder.</span>
            </div>
            <div class="sup-matrix-wrap" data-role="matrix" data-key="${escapeHtml(s.key)}">
              <div class="sup-matrix-loading">Loading discounts…</div>
            </div>
          </div>
          <div class="sup-sub" data-sub="contact" data-key="${escapeHtml(s.key)}" hidden>
            <dl class="sup-contact">
              <div><dt>Email</dt><dd><a href="mailto:${escapeHtml(s.contact.email)}">${escapeHtml(s.contact.email)}</a></dd></div>
              <div><dt>Phone</dt><dd><a href="tel:${escapeHtml(s.contact.phone.replace(/\s+/g, ""))}">${escapeHtml(s.contact.phone)}</a></dd></div>
              <div><dt>Website</dt><dd><a href="https://${escapeHtml(s.contact.website)}" target="_blank" rel="noopener noreferrer">${escapeHtml(s.contact.website)}</a></dd></div>
            </dl>
          </div>
        </section>`;
    }

    // Discount sheets, mounted the first time their supplier is shown.
    const sheets = new Set<string>();
    function ensureSheet(key: string): void {
      if (sheets.has(key)) return;
      const s = suppliers.find((x) => x.key === key);
      const host = panelsEl.querySelector<HTMLElement>(`.sup-sub[data-role='sheet'][data-key='${cssEscape(key)}']`);
      if (!s || !host) return;
      sheets.add(key);
      mountDiscountSheet(host, { supplierId: s.id, supplierName: s.name, ctx, signal: ac.signal });
    }

    // Any catalogue supplier the retailer has been granted beyond the seeded
    // four gets a tab of its own. Appended, never re-rendered, so a sheet
    // already open keeps its state.
    async function addGrantedSuppliers(): Promise<void> {
      try {
        const resp = await ctx.api<{ suppliers?: Array<{ id: string; slug: string; name: string; productCount?: number }> }>(
          "/catalogue/suppliers", { signal: ac.signal });
        if (disposed) return;
        const known = new Set(suppliers.map((s) => s.id));
        const extra = (resp.suppliers ?? []).filter((g) => g.id && !known.has(g.id));
        if (!extra.length) return;
        for (const g of extra) {
          const key = (g.slug || g.id).toUpperCase();
          const sup: Supplier = {
            key, id: g.id, name: g.name || key, tabLabel: g.name || key,
            desc: g.productCount ? `${g.productCount.toLocaleString("en-GB")} catalogue products` : "Catalogue supplier",
            colour: "#64748B", logo: key.slice(0, 3), profileUrl: "#",
            contact: { email: "", phone: "", website: "" },
          };
          suppliers.push(sup);
          panelsEl.insertAdjacentHTML("beforeend", panelHtml(sup));
          const panel = panelsEl.querySelector<HTMLElement>(`#sup-panel-${cssEscape(key)}`);
          if (panel) panel.hidden = true;
        }
        renderTabs();
      } catch { /* the seeded four still work */ }
    }

    function matrixWrap(key: string): HTMLElement | null {
      return panelsEl.querySelector<HTMLElement>(`.sup-matrix-wrap[data-key='${cssEscape(key)}']`);
    }

    function rowHtml(r: ClassRow): string {
      const toggle = r.hasSupplier
        ? `<label class="sup-toggle"><input type="checkbox" data-act="toggle" data-cat="${escapeHtml(r.categoryId)}" ${r.useSupplier ? "checked" : ""}/><span>Use supplier</span></label>`
        : `<span class="sup-muted">—</span>`;
      return `<tr data-cat="${escapeHtml(r.categoryId)}" data-name="${escapeHtml(r.className.toLowerCase())}">
        <td class="sup-mx-name">${escapeHtml(r.className)}</td>
        <td class="sup-mx-own"><input type="number" min="0" max="100" step="0.5" class="sup-mx-input"
          data-act="own" data-cat="${escapeHtml(r.categoryId)}" value="${r.retailerPct ?? ""}" placeholder="0" aria-label="Your discount for ${escapeHtml(r.className)}"/><span class="sup-mx-pct">%</span></td>
        <td class="sup-mx-sup">${r.supplierPct != null ? escapeHtml(fmtPct(r.supplierPct)) : `<span class="sup-muted">not set</span>`}</td>
        <td class="sup-mx-toggle">${toggle}</td>
        <td class="sup-mx-eff" data-role="eff" data-cat="${escapeHtml(r.categoryId)}">${escapeHtml(fmtPct(r.effectivePct))}</td>
      </tr>`;
    }

    function renderMatrix(key: string): void {
      const wrap = matrixWrap(key);
      if (!wrap) return;
      const state = loadState.get(key);
      if (state === "loading") { wrap.innerHTML = `<div class="sup-matrix-loading">Loading discounts…</div>`; return; }
      if (state === "error") { wrap.innerHTML = `<div class="sup-matrix-error">Couldn't load discounts. <button type="button" data-act="retry" data-key="${escapeHtml(key)}" class="sup-link">Retry</button></div>`; return; }
      const rows = matrix.get(key) ?? [];
      if (!rows.length) { wrap.innerHTML = `<div class="sup-matrix-empty">No product classes found for this supplier.</div>`; return; }
      wrap.innerHTML = `
        <table class="sup-matrix">
          <thead><tr><th>Product class</th><th>Your %</th><th>Supplier %</th><th>Source</th><th>Effective</th></tr></thead>
          <tbody>${rows.map(rowHtml).join("")}</tbody>
        </table>`;
    }

    async function loadMatrix(key: string): Promise<void> {
      const s = suppliers.find((x) => x.key === key);
      if (!s || loadState.get(key) === "loading" || loadState.get(key) === "ready") {
        if (loadState.get(key) === "ready") renderMatrix(key);
        return;
      }
      loadState.set(key, "loading"); renderMatrix(key);
      try {
        const resp = await ctx.api<MatrixResp>(`/portal/discounts?supplier=${encodeURIComponent(s.id)}`, { signal: ac.signal });
        if (disposed) return;
        matrix.set(key, resp.classes || []);
        loadState.set(key, "ready");
        renderMatrix(key);
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        loadState.set(key, "error");
        renderMatrix(key);
      }
    }

    function updateRowFromResp(key: string, c: PutResp["class"]): void {
      const rows = matrix.get(key); if (!rows) return;
      const row = rows.find((r) => r.categoryId === c.categoryId);
      if (!row) return;
      row.retailerPct = c.retailerPct; row.supplierPct = c.supplierPct;
      row.hasSupplier = c.hasSupplier; row.useSupplier = c.useSupplier; row.effectivePct = c.effectivePct;
      const effEl = matrixWrap(key)?.querySelector<HTMLElement>(`[data-role='eff'][data-cat='${cssEscape(c.categoryId)}']`);
      if (effEl) effEl.textContent = fmtPct(c.effectivePct);
    }

    async function save(key: string, categoryId: string, body: Record<string, unknown>): Promise<void> {
      const s = suppliers.find((x) => x.key === key); if (!s) return;
      try {
        const resp = await ctx.api<PutResp>("/portal/discounts", {
          method: "PUT", body: JSON.stringify({ supplier: s.id, categoryId, ...body }), signal: ac.signal });
        if (disposed) return;
        updateRowFromResp(key, resp.class);
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast(err instanceof ApiError ? `Couldn't save (HTTP ${err.status})` : "Couldn't save discount", "error");
      }
    }

    // ── events ──────────────────────────────────────────────────────────────────
    function onTabsClick(ev: MouseEvent): void {
      const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>(".sup-tab");
      if (btn?.dataset.key) setActive(btn.dataset.key);
    }
    function setActive(key: string): void {
      if (!suppliers.some((s) => s.key === key)) return;
      activeKey = key;
      for (const b of tabsEl.querySelectorAll<HTMLButtonElement>(".sup-tab")) {
        const a = b.dataset.key === key;
        b.classList.toggle("is-active", a); b.setAttribute("aria-selected", String(a)); b.tabIndex = a ? 0 : -1;
      }
      for (const s of suppliers) {
        const p = panelsEl.querySelector<HTMLElement>(`#sup-panel-${cssEscape(s.key)}`);
        if (p) p.hidden = s.key !== key;
      }
      ensureSheet(key);
      void loadMatrix(key);
    }

    function onPanelsClick(ev: MouseEvent): void {
      const t = ev.target as HTMLElement;
      const sub = t.closest<HTMLButtonElement>(".sup-subtab");
      if (sub) {
        const key = sub.dataset.key!; const which = sub.dataset.sub!;
        const panel = panelsEl.querySelector<HTMLElement>(`#sup-panel-${cssEscape(key)}`);
        panel?.querySelectorAll<HTMLElement>(".sup-subtab").forEach((b) => b.classList.toggle("is-active", b === sub));
        panel?.querySelectorAll<HTMLElement>(".sup-sub").forEach((p) => { p.hidden = p.dataset.sub !== which; });
        return;
      }
      const retry = t.closest<HTMLButtonElement>("[data-act='retry']");
      if (retry?.dataset.key) { loadState.set(retry.dataset.key, "idle"); void loadMatrix(retry.dataset.key); }
    }

    function onPanelsInput(ev: Event): void {
      const t = ev.target as HTMLElement;
      if (t instanceof HTMLInputElement && t.dataset.act === "own") {
        const key = (t.closest(".sup-sub") as HTMLElement)?.dataset.key; const cat = t.dataset.cat;
        if (!key || !cat) return;
        let v = parseFloat(t.value); if (!Number.isFinite(v)) v = 0; v = Math.max(0, Math.min(100, v));
        const tk = `${key}::${cat}`;
        const prev = saveTimers.get(tk); if (prev !== undefined) window.clearTimeout(prev);
        saveTimers.set(tk, window.setTimeout(() => {
          saveTimers.delete(tk);
          void save(key, cat, { discountPercent: v });
        }, 350));
      }
    }

    function onPanelsChange(ev: Event): void {
      const t = ev.target as HTMLElement;
      if (t instanceof HTMLInputElement && t.dataset.act === "toggle") {
        const key = (t.closest(".sup-sub") as HTMLElement)?.dataset.key; const cat = t.dataset.cat;
        if (key && cat) void save(key, cat, { useSupplier: t.checked });
      }
    }

    function onSearch(ev: Event): void {
      const inp = ev.target as HTMLElement;
      if (!(inp instanceof HTMLInputElement) || !inp.classList.contains("sup-matrix-search")) return;
      const key = inp.dataset.key; if (!key) return;
      const q = inp.value.trim().toLowerCase();
      const wrap = matrixWrap(key);
      wrap?.querySelectorAll<HTMLElement>("tbody tr").forEach((tr) => {
        tr.hidden = q !== "" && !(tr.dataset.name ?? "").includes(q);
      });
    }

    // ── wire-up ───────────────────────────────────────────────────────────────
    renderTabs();
    renderPanels();
    tabsEl.addEventListener("click", onTabsClick);
    panelsEl.addEventListener("click", onPanelsClick);
    panelsEl.addEventListener("input", onPanelsInput);
    panelsEl.addEventListener("input", onSearch);
    panelsEl.addEventListener("change", onPanelsChange);
    ensureSheet(activeKey);
    void loadMatrix(activeKey);
    void addGrantedSuppliers();

    return (): void => {
      disposed = true;
      tabsEl.removeEventListener("click", onTabsClick);
      panelsEl.removeEventListener("click", onPanelsClick);
      panelsEl.removeEventListener("input", onPanelsInput);
      panelsEl.removeEventListener("input", onSearch);
      panelsEl.removeEventListener("change", onPanelsChange);
      for (const t of saveTimers.values()) window.clearTimeout(t);
      saveTimers.clear();
      ac.abort();
    };
  },
};

registerPage(suppliersPage);
export default suppliersPage;
